package com.flowyride.feature.planner.ui.components

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.flowyride.core.domain.model.Place
import com.flowyride.feature.planner.StopUiState
import com.flowyride.feature.planner.resources.Res
import com.flowyride.feature.planner.resources.clear_field
import com.flowyride.feature.planner.resources.place_placeholder
import org.jetbrains.compose.resources.stringResource

/** Text field with an inline list of geocoding suggestions. */
@Composable
internal fun PlaceSearchField(
    label: String,
    markerColor: Color,
    state: StopUiState,
    onQueryChange: (String) -> Unit,
    onPlaceSelected: (Place) -> Unit,
    onClear: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier) {
        OutlinedTextField(
            value = state.query,
            onValueChange = onQueryChange,
            label = { Text(label) },
            placeholder = { Text(stringResource(Res.string.place_placeholder)) },
            singleLine = true,
            leadingIcon = { MarkerDot(markerColor) },
            trailingIcon = {
                when {
                    state.isSearching -> CircularProgressIndicator(Modifier.size(20.dp), strokeWidth = 2.dp)
                    state.query.isNotEmpty() -> {
                        val clearLabel = stringResource(Res.string.clear_field)
                        IconButton(onClick = onClear, modifier = Modifier.semantics { contentDescription = clearLabel }) {
                            Text("✕", style = MaterialTheme.typography.titleMedium)
                        }
                    }
                }
            },
            supportingText = state.selected?.detail?.let { detail -> { Text(detail, maxLines = 1, overflow = TextOverflow.Ellipsis) } },
            modifier = Modifier.fillMaxWidth(),
        )
        // Driven by state, not focus: losing focus on pointer-down would swallow the click.
        if (state.selected == null && state.suggestions.isNotEmpty()) {
            SuggestionList(state.suggestions, onPlaceSelected)
        }
    }
}

@Composable
private fun SuggestionList(suggestions: List<Place>, onPlaceSelected: (Place) -> Unit) {
    Column(Modifier.fillMaxWidth().padding(top = 4.dp)) {
        suggestions.forEachIndexed { index, place ->
            if (index > 0) HorizontalDivider()
            Column(
                Modifier
                    .fillMaxWidth()
                    .clickable { onPlaceSelected(place) }
                    .padding(horizontal = 12.dp, vertical = 8.dp),
            ) {
                Text(place.name, style = MaterialTheme.typography.bodyLarge, maxLines = 1, overflow = TextOverflow.Ellipsis)
                place.detail?.let {
                    Text(
                        it,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                    )
                }
            }
        }
    }
}

/** Same look as the map markers, so fields and map read together. */
@Composable
internal fun MarkerDot(color: Color, modifier: Modifier = Modifier) {
    Canvas(modifier.size(14.dp)) {
        drawCircle(Color.White)
        drawCircle(color, radius = size.minDimension / 2 - 2.dp.toPx())
    }
}
